import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDocument } from 'pdfjs-dist';
import '../utils/setupPdfWorker';

const MIN_SCALE = 0.6;
const MAX_SCALE = 3.0;
const DEFAULT_SCALE = 1.15;
const ZOOM_STEP = 0.2;

// PDF viewer with lazy page rendering using pdf.js
function _getTenantSlug() {
  try { return String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); } catch { return ''; }
}

export default function PdfViewer({ url, thumbUrl, className = '', onEdit, editLabel = 'Redigera', autoFitWidth = false, initialPage = null }) {
  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const pdfRef = useRef(null);
  const canvasRefs = useRef(new Map());
  const renderingPages = useRef(new Set());
  const renderedPages = useRef(new Set());
  const autoFitDoneRef = useRef('');
  /* Sätts först när handleFitWidth faktiskt har körts (inne i rAF) —
     autoFitDoneRef sätts synkront FÖRE de två animationsramarna och kan
     därför inte användas som gate för initialPage-hoppet. */
  const fitAppliedUrlRef = useRef('');
  const initialJumpDoneRef = useRef('');
  const [error, setError] = useState(null);
  const [pageNumbers, setPageNumbers] = useState([]);
  const [pageStatus, setPageStatus] = useState({});
  /* Sida 1:s basmått (scale 1). Ger orenderade sidor en realistisk
     platshållarhöjd — utan den är offsetTop 0 för allt orenderat, vilket
     gör sidhopp/skrollning meningslös och får lazy-renderingen att dra
     igång alla sidor på en gång. */
  const [baseSize, setBaseSize] = useState(null);
  /* Räknas upp när auto-fit har körts — låter initialPage-hoppet vänta in
     den utan att polla en ref (refs triggar inga effekter). */
  const [fitTick, setFitTick] = useState(0);
  const [rangeSupported, setRangeSupported] = useState(null);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');

  const INITIAL_RENDER_COUNT = 3;
  useEffect(() => {
    setScale(DEFAULT_SCALE);
  }, [url]);

  useEffect(() => {
    autoFitDoneRef.current = '';
    fitAppliedUrlRef.current = '';
    initialJumpDoneRef.current = '';
  }, [url]);

  useEffect(() => {
    if (!url) {
      setRangeSupported(null);
      return undefined;
    }
    const u = String(url || '');
    if (u.startsWith('blob:')) {
      setRangeSupported(true);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setRangeSupported('checking');
    (async () => {
      try {
        const _ts = _getTenantSlug();
        const resp = await fetch(url, {
          method: 'HEAD',
          headers: _ts ? { Range: 'bytes=0-0', 'X-Tenant-Slug': _ts } : { Range: 'bytes=0-0' },
          credentials: 'include',
          signal: controller.signal
        });
        if (cancelled) return;
        const acceptRanges = resp.headers.get('accept-ranges') || '';
        const supports = resp.status === 206 || /bytes/i.test(acceptRanges);
        setRangeSupported(supports);
      } catch (err) {
        if (!cancelled) {
          console.warn('PdfViewer: kunde inte verifiera Range-stöd', err);
          setRangeSupported(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [url]);

  const renderPage = useCallback(async (pageNumber) => {
    if (!pdfRef.current) return;
    if (renderingPages.current.has(pageNumber)) return;
    if (renderedPages.current.has(pageNumber)) return;

    const canvas = canvasRefs.current.get(pageNumber);
    if (!canvas) {
      renderingPages.current.delete(pageNumber);
      requestAnimationFrame(() => renderPage(pageNumber));
      return;
    }

    renderingPages.current.add(pageNumber);
    setPageStatus(prev => {
      if (prev[pageNumber] === 'rendering' || prev[pageNumber] === 'rendered') return prev;
      return { ...prev, [pageNumber]: 'rendering' };
    });

    try {
      const page = await pdfRef.current.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale });
      const context = canvas.getContext('2d');
      // Render at device pixel ratio for sharper output on HiDPI screens
      const outputScale = Math.min(2, window.devicePixelRatio || 1);
      const pixelWidth = Math.floor(viewport.width * outputScale);
      const pixelHeight = Math.floor(viewport.height * outputScale);
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      // Prevent global responsive CSS (e.g. canvas{max-width:100%}) from
      // shrinking width without matching height, which distorts PDF pages.
      canvas.style.maxWidth = 'none';
      canvas.style.maxHeight = 'none';
      canvas.dataset.baseWidth = String(baseViewport.width);
      const renderParams = { canvasContext: context, viewport };
      if (outputScale !== 1) {
        renderParams.transform = [outputScale, 0, 0, outputScale, 0, 0];
      }
      await page.render(renderParams).promise;
      page.cleanup();
      renderedPages.current.add(pageNumber);
      setPageStatus(prev => ({ ...prev, [pageNumber]: 'rendered' }));
    } catch (err) {
      console.warn('PdfViewer: kunde inte rendera sida', pageNumber, err);
      renderedPages.current.add(pageNumber);
      setPageStatus(prev => ({ ...prev, [pageNumber]: 'error' }));
    } finally {
      renderingPages.current.delete(pageNumber);
    }
  }, [scale]);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    pdfRef.current = null;
    canvasRefs.current.clear();
    setPageNumbers([]);
    setPageStatus({});
    setBaseSize(null);
    renderedPages.current.clear();
    setError(null);

    if (!url) return undefined;

    (async () => {
      try {
        const _slug = _getTenantSlug();
        loadingTask = getDocument({ url, withCredentials: true, ...(_slug ? { httpHeaders: { 'X-Tenant-Slug': _slug } } : {}) });
        const pdf = await loadingTask.promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }
        pdfRef.current = pdf;
        try {
          const firstPage = await pdf.getPage(1);
          const vp = firstPage.getViewport({ scale: 1 });
          if (!cancelled) setBaseSize({ width: vp.width, height: vp.height });
          firstPage.cleanup();
        } catch { /* platshållarhöjd är best-effort */ }
        if (cancelled) return;
        setPageNumbers(Array.from({ length: pdf.numPages }, (_, idx) => idx + 1));
      } catch (err) {
        if (!cancelled) {
          console.error('PdfViewer: kunde inte ladda PDF', err);
          setError('Kunde inte ladda PDF.');
        }
      }
    })();

    return () => {
      cancelled = true;
      renderingPages.current.clear();
      if (loadingTask) {
        loadingTask.destroy?.();
      }
      if (pdfRef.current) {
        pdfRef.current.destroy?.();
        pdfRef.current = null;
      }
    };
  }, [url]);

  useEffect(() => {
    if (!pageNumbers.length) return;
    const initial = pageNumbers.slice(0, INITIAL_RENDER_COUNT);
    initial.forEach(pageNumber => {
      requestAnimationFrame(() => renderPage(pageNumber));
    });
  }, [pageNumbers, renderPage]);

  useEffect(() => {
    if (!pageNumbers.length) return;
    if (!pdfRef.current) return;
    const pagesToRerender = Array.from(canvasRefs.current.keys());
    if (!pagesToRerender.length) return;
    renderedPages.current.clear();
    renderingPages.current.clear();
    setPageStatus(prev => {
      const next = { ...prev };
      pagesToRerender.forEach(pageNumber => {
        delete next[pageNumber];
      });
      return next;
    });
    pagesToRerender.forEach(pageNumber => {
      requestAnimationFrame(() => renderPage(pageNumber));
    });
  }, [scale, pageNumbers, renderPage]);

  useEffect(() => {
    const container = containerRef.current;
    const scrollRoot = scrollContainerRef.current;
    if (!container || !scrollRoot || !pageNumbers.length) return undefined;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const pageNumber = Number(entry.target.dataset.page);
          if (pageNumber) renderPage(pageNumber);
        }
      });
    }, { root: scrollRoot, rootMargin: '320px 0px', threshold: 0.1 });

    const elements = container.querySelectorAll('[data-page]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [pageNumbers, renderPage]);

  useEffect(() => {
    if (!pageNumbers.length) {
      setCurrentPage(1);
      setPageInput('1');
      return;
    }
    const firstPage = pageNumbers[0];
    setCurrentPage(firstPage);
    setPageInput(String(firstPage));
  }, [pageNumbers]);

  const updateCurrentFromScroll = useCallback(() => {
    const container = containerRef.current;
    const scrollRoot = scrollContainerRef.current;
    if (!container || !scrollRoot || !pageNumbers.length) return;
    const scrollTop = scrollRoot.scrollTop;
    const scrollRootTop = scrollRoot.getBoundingClientRect().top;
    let active = pageNumbers[0];
    let lastOffset = -Infinity;
    for (let i = 0; i < pageNumbers.length; i += 1) {
      const pageNumber = pageNumbers[i];
      const el = container.querySelector(`[data-page="${pageNumber}"]`);
      if (!el) continue;
      // Samma relativa mätning som goToPage — offsetTop pekar mot fel
      // förälder i modal-kontext.
      const offset = el.getBoundingClientRect().top - scrollRootTop + scrollTop;
      // Toleransen måste överstiga sidmellanrummet (mb-4 = 16px + kanter),
      // annars räknas sidan vars kant ligger precis under toppen inte som aktiv.
      if (offset > lastOffset && offset - 40 <= scrollTop) {
        active = pageNumber;
        lastOffset = offset;
      } else {
        break;
      }
    }
    setCurrentPage(prev => (prev === active ? prev : active));
  }, [pageNumbers]);

  useEffect(() => {
    const scrollRoot = scrollContainerRef.current;
    if (!scrollRoot) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        updateCurrentFromScroll();
      });
    };
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    updateCurrentFromScroll();
    return () => {
      scrollRoot.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [updateCurrentFromScroll]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const resolveThumb = useCallback((pageNumber) => {
    if (!thumbUrl) return null;
    if (typeof thumbUrl === 'function') {
      try {
        return thumbUrl(pageNumber);
      } catch {
        return null;
      }
    }
    if (thumbUrl.includes('{page}')) return thumbUrl.replace('{page}', pageNumber);
    if (thumbUrl.includes('%PAGE%')) return thumbUrl.replace(/%PAGE%/g, pageNumber);
    if (thumbUrl.includes('{index}')) return thumbUrl.replace('{index}', pageNumber - 1);
    return thumbUrl;
  }, [thumbUrl]);

  const pageItems = useMemo(() => pageNumbers.map(pageNumber => {
    const status = pageStatus[pageNumber];
    const fallbackSrc = status === 'error' ? resolveThumb(pageNumber) : null;
    const isActive = currentPage === pageNumber;
    const placeholderHeight = status !== 'rendered' && baseSize
      ? Math.round(baseSize.height * scale)
      : undefined;
    return (
      <div
        key={pageNumber}
        data-page={pageNumber}
        className={`relative mb-4 rounded border ${isActive ? 'border-emerald-500 shadow-md' : 'border-slate-200 shadow-sm'} bg-white dark:border-slate-700 dark:bg-slate-800`}
        style={placeholderHeight ? { minHeight: placeholderHeight } : undefined}
        aria-current={isActive ? 'page' : undefined}
      >
        <canvas
          ref={el => {
            if (el) {
              canvasRefs.current.set(pageNumber, el);
            } else {
              canvasRefs.current.delete(pageNumber);
            }
          }}
          className={(status === 'rendered' ? 'block' : 'hidden')}
        />
        {status !== 'rendered' && (
          <div className="absolute inset-0 flex items-center justify-center rounded text-xs text-slate-500 dark:text-slate-300">
            {status === 'error'
              ? (
                fallbackSrc ? (
                  <div className="flex flex-col items-center gap-2 p-2 text-center">
                    <img
                      src={fallbackSrc}
                      alt={`Förhandsbild sida ${pageNumber}`}
                      className="max-h-52 max-w-full rounded object-contain"
                    />
                    <span className="text-[11px] text-slate-500 dark:text-slate-300">Visar tumnagel – kunde inte rendera sidan.</span>
                  </div>
                ) : 'Kunde inte rendera sidan'
              )
              : 'Laddar sida…'}
          </div>
        )}
      </div>
    );
  }), [baseSize, currentPage, pageNumbers, pageStatus, resolveThumb, scale]);

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(MAX_SCALE, Number((prev + ZOOM_STEP).toFixed(2))));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(MIN_SCALE, Number((prev - ZOOM_STEP).toFixed(2))));
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(DEFAULT_SCALE);
  }, []);

  const handleFitWidth = useCallback(() => {
    const container = containerRef.current;
    const scrollRoot = scrollContainerRef.current;
    if (!container || !scrollRoot) return;
    const firstCanvas = container.querySelector('canvas');
    if (!firstCanvas) return;
    const baseWidth = Number(firstCanvas.dataset.baseWidth);
    if (!baseWidth) return;
    const availableWidth = scrollRoot.clientWidth || baseWidth;
    const nextScale = availableWidth / baseWidth;
    setScale(prev => {
      const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      return Number(clamped.toFixed(2)) || prev;
    });
  }, []);

  useEffect(() => {
    if (!autoFitWidth) return;
    if (!url) return;
    if (!pageNumbers.length) return;

    const firstPage = pageNumbers[0];
    if (pageStatus[firstPage] !== 'rendered') return;

    if (autoFitDoneRef.current === String(url)) return;
    autoFitDoneRef.current = String(url);

    // Defer to let layout settle (prevents measuring 0 width in rare cases).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          handleFitWidth();
        } catch {
          // ignore
        }
        fitAppliedUrlRef.current = String(url);
        setFitTick(t => t + 1);
      });
    });
  }, [autoFitWidth, handleFitWidth, pageNumbers, pageStatus, url]);

  const zoomDisplay = `${Math.round(scale * 100)} %`;
  const totalPages = pageNumbers.length;

  const goToPage = useCallback((target) => {
    if (!totalPages) return;
    const clamped = Math.min(totalPages, Math.max(1, target));
    const container = containerRef.current;
    const scrollRoot = scrollContainerRef.current;
    if (!container || !scrollRoot) return;
    const el = container.querySelector(`[data-page="${clamped}"]`);
    if (!el) return;
    // Position relativt skrollcontainern — offsetTop mäter mot närmaste
    // position:relative-förälder, vilket i modal-kontext är modalen (fel).
    const top = el.getBoundingClientRect().top
      - scrollRoot.getBoundingClientRect().top
      + scrollRoot.scrollTop;
    try {
      scrollRoot.scrollTo({ top, behavior: 'smooth' });
    } catch (err) {
      scrollRoot.scrollTop = top;
    }
    setCurrentPage(clamped);
    setPageInput(String(clamped));
  }, [totalPages]);

  const handlePrevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const handleNextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const handlePageInputChange = useCallback((e) => {
    setPageInput(e.target.value);
  }, []);

  const handlePageSubmit = useCallback((e) => {
    e.preventDefault();
    if (!totalPages) return;
    const parsed = Number(pageInput);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    goToPage(parsed);
  }, [currentPage, goToPage, pageInput, totalPages]);

  /* Hoppa till initialPage (t.ex. sidan med KanAI:s highlight-markering)
     när dokumentet öppnas. Målsidan måste renderas först — placeholder-
     sidor har ingen verklig höjd, så offsetTop är opålitlig innan dess. */
  useEffect(() => {
    const target = Number(initialPage) || 0;
    if (target <= 1) return;
    if (!pageNumbers.length) return;
    if (initialJumpDoneRef.current === String(url)) return;
    // Vänta in auto-fit — den ändrar skalan och därmed alla sidhöjder;
    // hoppar vi före landar vi på fel position. Gaten är fitAppliedUrlRef
    // (sätts när fit-körningen faktiskt skett), inte autoFitDoneRef (sätts
    // två animationsramar för tidigt).
    if (autoFitWidth && fitAppliedUrlRef.current !== String(url)) return;
    const clamped = Math.min(pageNumbers.length, target);
    if (pageStatus[clamped] !== 'rendered') {
      renderPage(clamped);
      return; // effekten körs om när pageStatus uppdateras
    }
    initialJumpDoneRef.current = String(url);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try { goToPage(clamped); } catch { /* ignore */ }
      });
    });
  }, [autoFitWidth, fitTick, initialPage, pageNumbers, pageStatus, url, renderPage, goToPage]);

  if (!url) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-white dark:bg-slate-800 ${className}`}>
        <div className="text-sm text-slate-500">Ingen förhandsvisning tillgänglig</div>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className={`h-full overflow-auto bg-white dark:bg-slate-900 p-2 ${className}`}>
      {error ? (
        <div className="w-full h-full flex items-center justify-center text-sm text-amber-700 dark:text-amber-300">{error}</div>
      ) : (
        <>
          <div
            className="sticky top-0 z-10 mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white/95 px-1 py-1 backdrop-blur supports-[backdrop-filter]:backdrop-blur dark:border-slate-700 dark:bg-slate-900/80"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleZoomOut}
                className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                aria-label="Zooma ut"
              >
                −
              </button>
              <span className="rounded border border-transparent px-2 py-1 text-xs text-slate-600 dark:text-slate-200" aria-live="polite">
                {zoomDisplay}
              </span>
              <button
                type="button"
                onClick={handleZoomIn}
                className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                aria-label="Zooma in"
              >
                +
              </button>
              <button
                type="button"
                onClick={handleResetZoom}
                className="ml-2 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                100%
              </button>
              <button
                type="button"
                onClick={handleFitWidth}
                className="ml-2 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Anpassa bredd
              </button>
            </div>
            {totalPages > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-200">
                <button
                  type="button"
                  onClick={handlePrevPage}
                  className="rounded border border-slate-300 px-2 py-1 font-medium hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                  disabled={currentPage <= 1}
                >
                  Föregående
                </button>
                <button
                  type="button"
                  onClick={handleNextPage}
                  className="rounded border border-slate-300 px-2 py-1 font-medium hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                  disabled={currentPage >= totalPages}
                >
                  Nästa
                </button>
                <form onSubmit={handlePageSubmit} className="flex items-center gap-1">
                  <label htmlFor="pdf-viewer-page-input" className="sr-only">Gå till sida</label>
                  <input
                    id="pdf-viewer-page-input"
                    type="number"
                    min={1}
                    max={totalPages}
                    value={pageInput}
                    onChange={handlePageInputChange}
                    className="w-16 rounded border border-slate-300 px-2 py-1 text-right text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    aria-label="Gå till sida"
                  />
                  <span className="text-xs text-slate-500 dark:text-slate-300">av {totalPages}</span>
                </form>
                <span className="rounded border border-transparent px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-200">
                  {currentPage} / {totalPages}
                </span>
              </div>
            )}
            {onEdit ? (
              <button
                type="button"
                onClick={onEdit}
                className="rounded border border-emerald-600 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-emerald-400 dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                aria-label={editLabel}
                title={editLabel}
              >
                {editLabel}
              </button>
            ) : (
              url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded border border-emerald-600 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-emerald-400 dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                >
                  Öppna original
                </a>
              )
            )}
          </div>
          {rangeSupported === false && (
            <div className="mb-2 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-500/60 dark:bg-amber-900/30 dark:text-amber-200">
              Servern saknar stöd för Range-requests. Stora PDF:er kan laddas långsammare.
            </div>
          )}
          <div ref={containerRef} data-testid="pdf-page-wrapper">
            {pageItems}
          </div>
        </>
      )}
    </div>
  );
}
